/** Recognisers used by global search to jump directly to a record. */
export const DEAL_NUMBER_RE = /^DG-\d{4}-\d{5}$/i;
export const EMIRATES_ID_RE = /^784-?\d{4}-?\d{7}-?\d$/;
export const INVOICE_RE = /^INV-[A-Z]+-\d{4}-\d{2}-\d{3}$/i;
export const PAYMENT_RE = /^PMT-[A-Z]+-\d{4}$/i;

/** "+971 50 123 4412" / "050 123 4412" -> "+971501234412" (or null). */
export function normaliseUaeMobile(input: string): string | null {
  const digits = input.replace(/[^\d+]/g, '');
  if (/^\+9715\d{8}$/.test(digits)) return digits;
  if (/^9715\d{8}$/.test(digits)) return `+${digits}`;
  if (/^05\d{8}$/.test(digits)) return `+971${digits.slice(1)}`;
  return null;
}

/** "784198712345673" -> "784-1987-1234567-3" */
export function formatEmiratesId(input: string): string {
  const d = input.replace(/\D/g, '');
  if (d.length !== 15) return input;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7, 14)}-${d.slice(14)}`;
}

/** Luhn (mod 10) over all digits: the last digit of an Emirates ID is its check digit (same rule as the API). */
export function luhnValid(digits: string): boolean {
  if (!/^\d+$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let n = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return sum % 10 === 0;
}

/**
 * An Emirates ID typed or pasted with or without separators ("784199259851230", "784-1992-5985123-0",
 * "784 1992 5985123 0") → "784-YYYY-NNNNNNN-C", or null when the structure (15 digits starting 784; only digits,
 * spaces and hyphens) or the check digit is wrong. Mirrors apps/api customers/domain/identity.ts.
 */
export function normalizeEmiratesId(input: string): string | null {
  const v = input.trim();
  if (!/^[\d\s-]+$/.test(v)) return null;
  const d = v.replace(/[\s-]/g, '');
  if (!/^784\d{12}$/.test(d) || !luhnValid(d)) return null;
  return formatEmiratesId(d);
}

/**
 * Progressive display while typing: digits grouped 3-4-7-1 as far as they go ("7841992" → "784-1992"). Text with
 * other characters is left as typed (validation reports it).
 */
export function formatEmiratesIdInput(input: string): string {
  if (!/^[\d\s-]*$/.test(input)) return input;
  const d = input.replace(/\D/g, '').slice(0, 15);
  const parts = [d.slice(0, 3), d.slice(3, 7), d.slice(7, 14), d.slice(14, 15)].filter(Boolean);
  return parts.join('-');
}

/** Groups an IBAN in fours for display: "AE070331234567890123456" -> "AE07 0331 2345 6789 0123 456" */
export function groupIban(iban: string): string {
  return iban.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

export function isValidUaeIban(iban: string): boolean {
  return /^AE\d{21}$/.test(iban.replace(/\s/g, ''));
}

/** A deterministic idempotency key for a mutation attempt. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
